export type OrganizationBillingStatus =
  | 'active'
  | 'past_due'
  | 'trialing'
  | 'unpaid'
  | 'canceled'
  | 'trial_expired'
  | string;

const APP_ACCESS_ALLOWED = new Set<OrganizationBillingStatus>(['active', 'past_due', 'trialing']);
const ACCOUNT_DEACTIVATED = new Set<OrganizationBillingStatus>(['canceled', 'unpaid', 'trial_expired']);
const PAID_FEATURES_BLOCKED = new Set<OrganizationBillingStatus>(['trialing', 'canceled', 'unpaid', 'trial_expired']);
const WHITE_LABEL_SETUP_ALLOWED = new Set<OrganizationBillingStatus>(['active', 'past_due', 'trialing']);

export function isPlatinumPlan(plan?: string | null): boolean {
  return plan === 'platinum';
}

/** White-label set-up is available on Platinum, or on any plan during a trial. */
export function canPurchaseWhiteLabelSetup(
  status?: OrganizationBillingStatus | null,
  plan?: string | null,
): boolean {
  if (!status || !WHITE_LABEL_SETUP_ALLOWED.has(status)) {
    return false;
  }
  return isPlatinumPlan(plan) || status === 'trialing';
}

export function canAccessAppForBillingStatus(status?: OrganizationBillingStatus | null): boolean {
  return !!status && APP_ACCESS_ALLOWED.has(status);
}

export function isAccountDeactivatedForBillingStatus(status?: OrganizationBillingStatus | null): boolean {
  return !!status && ACCOUNT_DEACTIVATED.has(status);
}

export function isPaidFeatureBlockedForBillingStatus(status?: OrganizationBillingStatus | null): boolean {
  return !!status && PAID_FEATURES_BLOCKED.has(status);
}

type PremiumTrackingUser = {
  uid?: string;
  trackingEnabled?: () => boolean;
} | null | undefined;

/** Self-paid personal premium tracking subscription (plan: personal, status: active). */
export function hasPersonalPremiumTracking(user: PremiumTrackingUser): boolean {
  return !!user?.trackingEnabled?.();
}

/** View access to own programs on a deactivated org when the user has personal premium tracking. */
export function canViewOwnProgramWithPremiumTracking(
  user: PremiumTrackingUser,
  programCreatedBy?: string | null
): boolean {
  return hasPersonalPremiumTracking(user) && !!user?.uid && user.uid === programCreatedBy;
}

export function isOrganizationProgramAccessBlocked(
  organizationStatus?: OrganizationBillingStatus | null,
  user?: PremiumTrackingUser,
  programCreatedBy?: string | null
): boolean {
  if (!isAccountDeactivatedForBillingStatus(organizationStatus)) {
    return false;
  }
  return !canViewOwnProgramWithPremiumTracking(user, programCreatedBy);
}

export function isOrgAssignedUserProgramListed(
  program: {
    organization?: string | null;
    createdBy?: string | null;
    isStartDateValid?: () => boolean;
    isEndDateValid?: () => boolean;
  },
  user: PremiumTrackingUser,
  organizationId?: string | null,
  organizationStatus?: OrganizationBillingStatus | null,
  userUid?: string | null
): boolean {
  if (!program.organization || program.organization !== organizationId) {
    return false;
  }
  if (isAccountDeactivatedForBillingStatus(organizationStatus)) {
    return canViewOwnProgramWithPremiumTracking(user, program.createdBy);
  }
  return program.createdBy === userUid || (
    !!program.isStartDateValid?.() && !!program.isEndDateValid?.()
  );
}

export function isPremiumUserProgramListed(
  program: { isPremium?: boolean; createdBy?: string | null },
  user: PremiumTrackingUser,
  organizationStatus?: OrganizationBillingStatus | null,
  userUid?: string | null,
  hasAppTrackingEnabled = false
): boolean {
  if (!program.isPremium || program.createdBy !== userUid) {
    return false;
  }
  if (isAccountDeactivatedForBillingStatus(organizationStatus)) {
    return hasPersonalPremiumTracking(user);
  }
  return hasAppTrackingEnabled;
}
